import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Trash2, Plus, Eye, EyeOff, CheckCircle2, AlertCircle, Info, X, ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import StrideToggle from "../../UI/Toggle";
import { ACTIVITY_LABELS, OUTCOME_STYLES } from "./constants";
import { GLOSSARY } from "./glossary";

// Small form controls shared by the Product Feeds pages. They follow the
// Tailwind styles used across FunctionDetails.

const inputClass = "block px-2.5 py-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-2 appearance-none focus:outline-none focus:border-primary disabled:bg-gray-100";

// ── Explanations (ⓘ) ─────────────────────────────────────────────────────────

const POPOVER_MARGIN = 8;

// Glossary lookups use config values as keys; names such as "constructor"
// would find Object.prototype members, which aren't content.
const hasTip = (content) => !!content && typeof content !== "function";

// Below the icon, or above it when there's no room, and kept inside the viewport.
const placePopover = (anchor, panel) => {
    const rect = anchor.getBoundingClientRect();
    const { width, height } = panel.getBoundingClientRect();
    const below = rect.bottom + POPOVER_MARGIN;
    const top = below + height <= window.innerHeight - POPOVER_MARGIN ? below : Math.max(POPOVER_MARGIN, rect.top - height - POPOVER_MARGIN);
    const left = Math.max(POPOVER_MARGIN, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - POPOVER_MARGIN));
    return { top, left };
};

/**
 * An ⓘ button that opens an explanation when clicked. It closes on a second
 * click, a click elsewhere or Escape. The panel is portalled to <body> so
 * tables, drawers and modals don't clip it; long explanations scroll inside it.
 */
export const InfoTip = ({ content, wide = false, size = 14, className = "" }) => {
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const enabled = hasTip(content);

    const reposition = useCallback(() => {
        if (buttonRef.current && panelRef.current) setPosition(placePopover(buttonRef.current, panelRef.current));
    }, []);

    useLayoutEffect(() => {
        if (open) reposition();
        else setPosition(null);
    }, [open, reposition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (buttonRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;
            setOpen(false);
            buttonRef.current?.focus();
        };
        // Follow the icon when the page, a drawer or a table scrolls; the panel's own scrolling is ignored.
        const onScroll = (event) => {
            if (!panelRef.current?.contains(event.target)) reposition();
        };
        document.addEventListener("mousedown", onPointerDown, true);
        document.addEventListener("keydown", onKeyDown);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", reposition);
        return () => {
            document.removeEventListener("mousedown", onPointerDown, true);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open, reposition]);

    // Grid header cells sort on Enter, so Enter and Space stop at the button.
    useEffect(() => {
        const button = buttonRef.current;
        if (!button) return undefined;
        const stop = (event) => {
            if (event.key === "Enter" || event.key === " ") event.stopPropagation();
        };
        button.addEventListener("keydown", stop);
        return () => button.removeEventListener("keydown", stop);
    }, [enabled]);

    if (!enabled) return null;
    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                aria-label="What does this mean?"
                aria-expanded={open}
                onClick={(event) => {
                    event.stopPropagation();
                    setOpen((value) => !value);
                }}
                className={`inline-flex shrink-0 align-middle rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary hover:text-primary ${open ? "text-primary" : "text-gray-400"} ${className}`}
            >
                <Info size={size} />
            </button>
            {open &&
                ReactDOM.createPortal(
                    <div
                        ref={panelRef}
                        role="dialog"
                        aria-label="Explanation"
                        style={position || { top: 0, left: 0, visibility: "hidden" }}
                        className={`fixed z-[999999] w-max ${wide ? "max-w-md" : "max-w-xs"} max-h-[min(60vh,420px)] overflow-y-auto px-3 py-2 rounded-lg shadow-lg bg-gray-900 text-white text-xs font-normal leading-relaxed text-left normal-case whitespace-normal break-words`}
                    >
                        {content}
                    </div>,
                    document.body
                )}
        </>
    );
};

// Something followed by its ⓘ.
export const WithInfo = ({ tip, children, wide = false, className = "" }) => (
    <span className={`inline-flex items-center gap-1 ${className}`}>
        {children}
        <InfoTip content={tip} wide={wide} />
    </span>
);

/**
 * Explanation listing every option of a choice.
 *
 * @param {string} [intro]
 * @param {object} descriptions - { value: text }
 * @param {object} [labels] - { value: label }; the value is shown when missing
 */
export const optionsTip = (intro, descriptions = {}, labels = {}) => (
    <div className="flex flex-col gap-1">
        {intro && <p>{intro}</p>}
        {Object.entries(descriptions).map(([value, text]) => (
            <p key={value}>
                <span className="font-semibold">{labels[value] || value}:</span> {text}
            </p>
        ))}
    </div>
);

export const outcomesTip = (intro) => optionsTip(intro, GLOSSARY.outcomes);

/**
 * AG Grid header with an ⓘ: use as headerComponent with
 * headerComponentParams: { tip }. Clicking the label sorts, like the default header.
 */
export const GridHeader = ({ displayName, enableSorting, progressSort, column, tip }) => {
    const [sort, setSort] = useState(() => column?.getSort?.());
    useEffect(() => {
        if (!column) return undefined;
        const update = () => setSort(column.getSort());
        column.addEventListener("sortChanged", update);
        return () => column.removeEventListener("sortChanged", update);
    }, [column]);

    return (
        <div className="flex items-center gap-1 w-full min-w-0">
            <span
                className={`ag-header-cell-text truncate ${enableSorting ? "cursor-pointer" : ""}`}
                onClick={(event) => enableSorting && progressSort(event.shiftKey)}
            >
                {displayName}
            </span>
            {sort === "asc" && <ArrowUp size={12} className="shrink-0 text-gray-500" />}
            {sort === "desc" && <ArrowDown size={12} className="shrink-0 text-gray-500" />}
            <InfoTip content={tip} wide />
        </div>
    );
};

// ── Controls ─────────────────────────────────────────────────────────────────

// The ⓘ after the tabs explains the open tab.
export const TabBar = ({ tabs, active, onChange, className = "" }) => (
    <div className={`flex flex-wrap items-center border-b border-gray-200 ${className}`}>
        {tabs.map((tab) => (
            <button
                key={tab.id}
                type="button"
                onClick={() => onChange(tab.id)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    active === tab.id ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
                {tab.label}
            </button>
        ))}
        <InfoTip content={tabs.find((tab) => tab.id === active)?.tip} wide className="ml-2" />
    </div>
);

// A div rather than a <label>: several fields wrap more than one control.
export const Field = ({ label, help, tip, children, className = "" }) => (
    <div className={`flex flex-col gap-1 text-sm ${className}`}>
        {label && (
            <span className="font-medium text-gray-700 flex items-center gap-1">
                {label}
                <InfoTip content={tip} wide />
            </span>
        )}
        {children}
        {help && <span className="text-xs text-gray-400">{help}</span>}
    </div>
);

export const TextInput = ({ value, onChange, placeholder, disabled, className = "", monospace = false, type = "text" }) => (
    <input
        type={type}
        className={`${inputClass} ${monospace ? "font-mono" : ""} ${className}`}
        value={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
    />
);

export const NumberInput = ({ value, onChange, placeholder, min, max, className = "" }) => (
    <input
        type="number"
        className={`${inputClass} ${className}`}
        value={value ?? ""}
        placeholder={placeholder}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
    />
);

export const SelectInput = ({ value, onChange, options, placeholder, disabled, className = "" }) => (
    <select className={`${inputClass} ${className}`} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => {
            const item = typeof option === "string" ? { value: option, label: option } : option;
            return (
                <option key={item.value} value={item.value}>
                    {item.label}
                </option>
            );
        })}
    </select>
);

export const Toggle = ({ checked, onChange, label, help, tip }) => (
    <div className="flex items-center gap-3">
        <label className="flex items-center gap-3 cursor-pointer">
            <StrideToggle
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                size="md"
            />
            {(label || help) && (
                <div>
                    {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
                    {help && <p className="text-xs text-gray-400">{help}</p>}
                </div>
            )}
        </label>
        <InfoTip content={tip} wide />
    </div>
);

export const IconButton = ({ title, onClick, children, disabled, className = "" }) => (
    <button
        type="button"
        title={title}
        aria-label={title}
        disabled={disabled}
        onClick={onClick}
        className={`p-1.5 rounded-md text-gray-500 hover:text-primary hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    >
        {children}
    </button>
);

export const LinkButton = ({ onClick, children, className = "", disabled }) => (
    <button type="button" disabled={disabled} onClick={onClick} className={`text-sm font-medium text-primary hover:underline disabled:opacity-50 flex items-center gap-1 ${className}`}>
        {children}
    </button>
);

/**
 * Editable list of { key, value } pairs. With `allowSecret`, values can be
 * marked secret; the server stores those encrypted and returns them masked.
 */
export const KeyValueEditor = ({ items = [], onChange, allowSecret = false, keyPlaceholder = "Name", valuePlaceholder = "Value" }) => {
    const [visible, setVisible] = useState({});
    const update = (index, patch) => onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    return (
        <div className="flex flex-col gap-2">
            {items.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                    <TextInput value={item.key} placeholder={keyPlaceholder} onChange={(key) => update(index, { key })} className="max-w-[40%]" />
                    <TextInput
                        type={item.secret && !visible[index] ? "password" : "text"}
                        value={item.value}
                        placeholder={valuePlaceholder}
                        onChange={(value) => update(index, { value })}
                    />
                    {allowSecret && (
                        <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                            <input type="checkbox" checked={!!item.secret} onChange={(e) => update(index, { secret: e.target.checked })} />
                            Secret
                        </label>
                    )}
                    {allowSecret && item.secret && (
                        <IconButton title={visible[index] ? "Hide" : "Show"} onClick={() => setVisible({ ...visible, [index]: !visible[index] })}>
                            {visible[index] ? <EyeOff size={16} /> : <Eye size={16} />}
                        </IconButton>
                    )}
                    <IconButton title="Remove" onClick={() => onChange(items.filter((_, i) => i !== index))}>
                        <Trash2 size={16} />
                    </IconButton>
                </div>
            ))}
            <LinkButton onClick={() => onChange([...items, { key: "", value: "" }])}>
                <Plus size={14} /> Add
            </LinkButton>
        </div>
    );
};

/**
 * Raw JSON editor for any part of the config the forms don't cover.
 */
export const JsonEditor = ({ value, onChange, rows = 12, className = "" }) => {
    const [text, setText] = useState(() => JSON.stringify(value ?? null, null, 2));
    const [error, setError] = useState("");
    const [status, showStatus] = useStatus(3000);

    useEffect(() => {
        setText(JSON.stringify(value ?? null, null, 2));
        setError("");
    }, [value]);

    // Applied when the editor loses focus.
    const apply = () => {
        if (text === JSON.stringify(value ?? null, null, 2)) return;
        try {
            const parsed = JSON.parse(text);
            setError("");
            onChange(parsed);
            showStatus("JSON applied");
        } catch (e) {
            setError(`Invalid JSON: ${e.message}. Changes not applied.`);
        }
    };

    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            <textarea
                className={`${inputClass} font-mono text-xs`}
                rows={rows}
                spellCheck={false}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={apply}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
            <StatusMessage status={status} />
        </div>
    );
};

export const Drawer = ({ title, onClose, children, footer, widthClass = "max-w-[1400px]" }) =>
    ReactDOM.createPortal(
        <div className="fixed inset-0 z-[999] bg-black-overlay flex justify-end" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className={`bg-white h-full w-full ${widthClass} shadow-xl flex flex-col`}>
                <div className="flex items-center justify-between px-5 py-4 bg-primary">
                    <h2 className="text-xl font-bold text-white truncate">{title}</h2>
                    <button type="button" className="w-8 h-8 inline-flex justify-center items-center" onClick={onClose}>
                        <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                            <path stroke="#fff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                        </svg>
                        <span className="sr-only">Close</span>
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">{children}</div>
                {footer && <div className="border-t border-gray-200 px-5 py-3 flex justify-end gap-3">{footer}</div>}
            </div>
        </div>,
        document.body
    );

export const OutcomeBadge = ({ outcome }) => (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${OUTCOME_STYLES[outcome] || "bg-gray-100 text-gray-600"}`}>{outcome || "—"}</span>
);

// A build or push in progress (feed.activity), with a spinner.
export const ActivityBadge = ({ activity }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${OUTCOME_STYLES.running}`}>
        <Loader2 size={12} className="animate-spin shrink-0" aria-hidden="true" />
        {ACTIVITY_LABELS[activity?.stage] || "Running"}…
    </span>
);

export const Section = ({ title, description, tip, actions, children, className = "" }) => (
    <section className={`border border-gray-200 rounded-lg p-4 flex flex-col gap-3 ${className}`}>
        {(title || actions) && (
            <div className="flex items-start justify-between gap-4">
                <div>
                    {title && (
                        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
                            {title}
                            <InfoTip content={tip} wide />
                        </h3>
                    )}
                    {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
                </div>
                {actions && <div className="flex items-center gap-2">{actions}</div>}
            </div>
        )}
        {children}
    </section>
);

export const IssueList = ({ errors = [], warnings = [], title }) => {
    if (!errors.length && !warnings.length) return null;
    return (
        <div className="flex flex-col gap-1 text-sm">
            {title && <p className="font-medium text-gray-700">{title}</p>}
            {errors.map((issue, i) => (
                <p key={`e${i}`} className="text-red-600">
                    <span className="font-mono text-xs">{issue.at}</span> {issue.message}
                </p>
            ))}
            {warnings.map((issue, i) => (
                <p key={`w${i}`} className="text-yellow-700">
                    <span className="font-mono text-xs">{issue.at}</span> {issue.message}
                </p>
            ))}
        </div>
    );
};

export const Stat = ({ label, value, tip }) => (
    <div className="flex flex-col px-3 py-2 bg-gray-50 rounded-md min-w-[110px]">
        <span className="text-xs text-gray-500 flex items-center gap-1">
            {label}
            <InfoTip content={tip} />
        </span>
        <span className="text-base font-semibold text-gray-900">{value ?? "—"}</span>
    </div>
);

/**
 * Short-lived feedback for a button press. Success and info messages clear
 * themselves; errors stay until the next action.
 *
 * @returns {[object|null, Function, Function]} [status, show(message, type), clear()]
 */
export const useStatus = (timeoutMs = 6000) => {
    const [status, setStatus] = useState(null);
    const timer = useRef(null);

    const clear = useCallback(() => {
        clearTimeout(timer.current);
        setStatus(null);
    }, []);

    const show = useCallback((message, type = "success") => {
        clearTimeout(timer.current);
        setStatus({ message, type, at: Date.now() });
        if (type !== "error") timer.current = setTimeout(() => setStatus(null), timeoutMs);
    }, [timeoutMs]);

    useEffect(() => () => clearTimeout(timer.current), []);
    return [status, show, clear];
};

const STATUS_STYLES = {
    success: { className: "text-[#27942B]", Icon: CheckCircle2 },
    error: { className: "text-red-500", Icon: AlertCircle },
    info: { className: "text-gray-600", Icon: Info },
};

export const StatusMessage = ({ status, onDismiss, className = "" }) => {
    if (!status) return null;
    const { className: tone, Icon } = STATUS_STYLES[status.type] || STATUS_STYLES.info;
    return (
        <p key={status.at} role="status" aria-live="polite" className={`text-sm flex items-start gap-1.5 ${tone} ${className}`}>
            <Icon size={16} className="shrink-0 mt-0.5" />
            <span className="break-words">{status.message}</span>
            {onDismiss && (
                <button type="button" title="Dismiss" className="ml-1 text-gray-400 hover:text-gray-600" onClick={onDismiss}>
                    <X size={14} />
                </button>
            )}
        </p>
    );
};
